import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, LogOut, Plus, Pencil, Trash2, Eye, EyeOff, MessageSquare, Users } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function getAuthHeaders() {
  const token = localStorage.getItem("admin_token");
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
}

export default function AdminDashboard() {
  const [tab, setTab] = useState("posts");
  const [posts, setPosts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", category: "", content: "", excerpt: "", read_time: "5 min read", published: false });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchPosts = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/blog?published_only=false`, { headers: getAuthHeaders(), credentials: "include" });
      if (res.ok) setPosts(await res.json());
    } catch { /* silent */ }
  }, []);

  const fetchContacts = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/contacts`, { headers: getAuthHeaders(), credentials: "include" });
      if (res.ok) setContacts(await res.json());
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) { navigate("/admin"); return; }
    // Verify token
    fetch(`${BACKEND_URL}/api/auth/me`, { headers: getAuthHeaders(), credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => { fetchPosts(); fetchContacts(); setLoading(false); })
      .catch(() => { localStorage.removeItem("admin_token"); navigate("/admin"); });
  }, [navigate, fetchPosts, fetchContacts]);

  const handleSave = async () => {
    const url = editing ? `${BACKEND_URL}/api/blog/${editing}` : `${BACKEND_URL}/api/blog`;
    const method = editing ? "PUT" : "POST";
    try {
      const res = await fetch(url, { method, headers: getAuthHeaders(), credentials: "include", body: JSON.stringify(form) });
      if (res.ok) {
        setForm({ title: "", category: "", content: "", excerpt: "", read_time: "5 min read", published: false });
        setEditing(null);
        fetchPosts();
      }
    } catch { /* silent */ }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await fetch(`${BACKEND_URL}/api/blog/${id}`, { method: "DELETE", headers: getAuthHeaders(), credentials: "include" });
      fetchPosts();
    } catch { /* silent */ }
  };

  const startEdit = (post) => {
    setEditing(post.id);
    setForm({ title: post.title, category: post.category, content: post.content, excerpt: post.excerpt, read_time: post.read_time, published: post.published });
    setTab("editor");
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  if (loading) return <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center"><p className="text-[#6B7280]">Loading...</p></div>;

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E7EB] px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between h-14">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#06B6D4]" />
            <span className="font-['Outfit'] font-bold text-[#0F172A]">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="text-sm text-[#6B7280] hover:text-[#111827]" data-testid="admin-view-site">View Site</button>
            <button onClick={handleLogout} className="inline-flex items-center text-sm text-[#6B7280] hover:text-red-500" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: "posts", label: "Blog Posts", icon: Eye },
            { id: "editor", label: editing ? "Edit Post" : "New Post", icon: Plus },
            { id: "contacts", label: "Messages", icon: MessageSquare },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                tab === t.id ? "bg-[#0F172A] text-white" : "bg-white text-[#6B7280] border border-[#E5E7EB] hover:text-[#111827]"
              }`}
            >
              <t.icon className="w-4 h-4 mr-1.5" /> {t.label}
            </button>
          ))}
        </div>

        {/* Blog Posts List */}
        {tab === "posts" && (
          <div className="space-y-3" data-testid="admin-posts-list">
            {posts.length === 0 && <p className="text-sm text-[#6B7280]">No blog posts yet. Create your first one!</p>}
            {posts.map((post) => (
              <div key={post.id} className="bg-white border border-[#E5E7EB] rounded-xl p-4 flex items-center justify-between" data-testid={`admin-post-${post.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-['Outfit'] font-bold text-sm text-[#111827] truncate">{post.title}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${post.published ? "bg-green-50 text-green-600 border border-green-200" : "bg-yellow-50 text-yellow-600 border border-yellow-200"}`}>
                      {post.published ? "Published" : "Draft"}
                    </span>
                  </div>
                  <p className="text-xs text-[#6B7280]">{post.category} &middot; {post.read_time}</p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button onClick={() => startEdit(post)} className="p-1.5 rounded-lg hover:bg-[#F9FAFB]" data-testid={`admin-edit-${post.id}`}><Pencil className="w-4 h-4 text-[#6B7280]" /></button>
                  <button onClick={() => handleDelete(post.id)} className="p-1.5 rounded-lg hover:bg-red-50" data-testid={`admin-delete-${post.id}`}><Trash2 className="w-4 h-4 text-red-400" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Post Editor */}
        {tab === "editor" && (
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 space-y-4" data-testid="admin-post-editor">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#111827] mb-1.5 block">Title</label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Post title" className="bg-white border-[#E5E7EB]" data-testid="admin-post-title" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#111827] mb-1.5 block">Category</label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. EDR, SIEM, Cloud" className="bg-white border-[#E5E7EB]" data-testid="admin-post-category" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#111827] mb-1.5 block">Excerpt</label>
              <Input value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} placeholder="Brief summary" className="bg-white border-[#E5E7EB]" data-testid="admin-post-excerpt" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#111827] mb-1.5 block">Content (Markdown)</label>
              <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={12} placeholder="Write your post in markdown..." className="bg-white border-[#E5E7EB] font-mono text-sm" data-testid="admin-post-content" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#111827] mb-1.5 block">Read Time</label>
                <Input value={form.read_time} onChange={(e) => setForm({ ...form, read_time: e.target.value })} placeholder="5 min read" className="bg-white border-[#E5E7EB]" data-testid="admin-post-readtime" />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="rounded border-[#E5E7EB]" data-testid="admin-post-published" />
                  <span className="text-sm font-medium text-[#111827]">Publish immediately</span>
                </label>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={handleSave}
                data-testid="admin-post-save"
                className="inline-flex items-center px-6 py-2.5 rounded-lg bg-[#0F172A] text-white text-sm font-medium transition-all hover:bg-[#111827]"
              >
                {editing ? "Update Post" : "Create Post"}
              </button>
              {editing && (
                <button
                  onClick={() => { setEditing(null); setForm({ title: "", category: "", content: "", excerpt: "", read_time: "5 min read", published: false }); }}
                  className="px-6 py-2.5 rounded-lg bg-white text-[#6B7280] border border-[#E5E7EB] text-sm font-medium hover:text-[#111827]"
                  data-testid="admin-post-cancel"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        )}

        {/* Contacts */}
        {tab === "contacts" && (
          <div className="space-y-3" data-testid="admin-contacts-list">
            {contacts.length === 0 && <p className="text-sm text-[#6B7280]">No messages yet.</p>}
            {contacts.map((c) => (
              <div key={c.id} className="bg-white border border-[#E5E7EB] rounded-xl p-4" data-testid={`admin-contact-${c.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-['Outfit'] font-bold text-sm text-[#111827]">{c.name}</h3>
                  <p className="text-xs text-[#6B7280]">{c.email} {c.company && <span>&middot; {c.company}</span>}</p>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#06B6D4]/10 text-[#06B6D4]">{c.enquiry_type}</span>
                </div>
                <p className="text-sm text-[#6B7280] mt-2">{c.message}</p>
                <p className="text-xs text-[#6B7280]/60 mt-2">{c.created_at?.split("T")[0]}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
